import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal, PenLine, Plus, UserCheck, UserX, Users } from 'lucide-react';
import { User } from '../../types/auth';
import { userService, UserListQuery } from '../../services/userService';
import {
  Button,
  Card,
  Dot,
  EmptyState,
  IconButton,
  Menu,
  MenuItem,
  PageHeader,
  Pill,
  SearchInput,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  roleTone,
} from '../ui';

interface UserListProps {
  onEditUser: (user: User) => void;
  onCreateUser: () => void;
  refreshTrigger?: number;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  top_owner: 'Top Owner',
  user: 'User',
};

const formatRoleName = (role: string) =>
  ROLE_LABELS[role] ?? role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const displayName = (user: User) =>
  user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.email;

const initial = (user: User) => (user.firstName || user.lastName || user.email || '?').trim().charAt(0).toUpperCase();

function formatRelative(iso?: string): string {
  if (!iso) return 'Never';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const Avatar: React.FC<{ user: User }> = ({ user }) => (
  <span
    aria-hidden="true"
    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-[12px] font-medium text-neutral-600"
  >
    {initial(user)}
  </span>
);

const RolePill: React.FC<{ role: string }> = ({ role }) => (
  <Pill tone={roleTone(role)}>{formatRoleName(role)}</Pill>
);

export const UserList: React.FC<UserListProps> = ({
  onEditUser,
  onCreateUser,
  refreshTrigger = 0
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<UserListQuery>({
    page: 1,
    limit: 20
  });
  const [total, setTotal] = useState(0);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getUsers(query);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, refreshTrigger]);

  const handleToggleUserStatus = async (user: User) => {
    try {
      if (user.isActive) {
        await userService.deactivateUser(user.id);
      } else {
        await userService.activateUser(user.id);
      }
      await loadUsers();
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Failed to update user status');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleRoleFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value || undefined;
    setQuery(prev => ({ ...prev, role, page: 1 }));
  };

  const handleStatusFilter = (value: StatusFilter) => {
    const isActive = value === 'all' ? undefined : value === 'active';
    setQuery(prev => ({ ...prev, isActive, page: 1 }));
  };

  const statusValue: StatusFilter = query.isActive === undefined ? 'all' : query.isActive ? 'active' : 'inactive';
  const hasFilters = !!(query.search || query.role || query.isActive !== undefined);
  const page = query.page ?? 1;
  const limit = query.limit ?? 20;
  const initialLoad = loading && users.length === 0;

  const menuItems = (user: User): MenuItem[] => [
    { label: 'Edit', icon: PenLine, onSelect: () => onEditUser(user) },
    user.isActive
      ? { label: 'Deactivate', icon: UserX, danger: true, onSelect: () => handleToggleUserStatus(user) }
      : { label: 'Activate', icon: UserCheck, onSelect: () => handleToggleUserStatus(user) },
  ];

  const rowMenu = (user: User) => (
    // Keep menu clicks from opening the row
    <div onClick={e => e.stopPropagation()}>
      <Menu
        items={menuItems(user)}
        trigger={props => (
          <IconButton label={`Actions for ${displayName(user)}`} size="sm" className="h-10 w-10 sm:h-8 sm:w-8" {...props}>
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        )}
      />
    </div>
  );

  return (
    <>
      <PageHeader
        title="Users"
        meta={initialLoad ? ' ' : `${total} ${total === 1 ? 'user' : 'users'}`}
        actions={
          <Button size="sm" variant="primary" onClick={onCreateUser}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            Add
          </Button>
        }
      />

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput
          id="search"
          aria-label="Search users"
          className="w-full sm:w-72"
          placeholder="Search"
          value={query.search || ''}
          onChange={handleSearchChange}
        />
        <SegmentedControl<StatusFilter>
          label="Status"
          value={statusValue}
          onChange={handleStatusFilter}
          items={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
        <Select
          id="roleFilter"
          aria-label="Role"
          className="min-w-0 flex-1 sm:w-36 sm:flex-none"
          value={query.role || ''}
          onChange={handleRoleFilter}
        >
          <option value="">All roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="top_owner">Top Owner</option>
          <option value="user">User</option>
        </Select>
      </div>

      {error && (
        <p role="alert" className="mb-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      <Card clip className={`transition-opacity ${loading && !initialLoad ? 'opacity-60' : ''}`}>
        {initialLoad ? (
          <div className="divide-y divide-neutral-100">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-40" />
                  <Skeleton className="h-3 w-56" />
                </div>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title={hasFilters ? 'No matches' : 'No users yet'}
            action={
              !hasFilters && (
                <Button size="sm" variant="secondary" onClick={onCreateUser}>
                  Add user
                </Button>
              )
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto sm:block">
              <Table>
                <THead>
                  <tr>
                    <TH>Name</TH>
                    <TH className="hidden lg:table-cell">Email</TH>
                    <TH>Role</TH>
                    <TH>Status</TH>
                    <TH>Last login</TH>
                    <TH className="w-12">
                      <span className="sr-only">Actions</span>
                    </TH>
                  </tr>
                </THead>
                <TBody>
                  {users.map(user => (
                    <TR key={user.id} interactive onClick={() => onEditUser(user)}>
                      <TD className="max-w-[260px]">
                        <div className="flex min-w-0 items-center gap-3">
                          <Avatar user={user} />
                          <div className="min-w-0">
                            <p className="truncate text-ink">{displayName(user)}</p>
                            <p className="truncate text-[12px] text-neutral-400 lg:hidden">{user.email}</p>
                          </div>
                        </div>
                      </TD>
                      <TD className="hidden max-w-[260px] truncate text-neutral-500 lg:table-cell">{user.email}</TD>
                      <TD>
                        <RolePill role={user.role} />
                      </TD>
                      <TD>
                        <span className={`inline-flex items-center gap-2 ${user.isActive ? 'text-ink' : 'text-neutral-400'}`}>
                          <Dot tone={user.isActive ? 'green' : 'neutral'} />
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </TD>
                      <TD
                        className="whitespace-nowrap tabular-nums text-neutral-500"
                        title={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : undefined}
                      >
                        {formatRelative(user.lastLoginAt)}
                      </TD>
                      <TD className="pr-2 text-right">{rowMenu(user)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>

            {/* Phone rows */}
            <ul className="divide-y divide-neutral-200/70 sm:hidden">
              {users.map(user => (
                <li key={user.id} className="flex items-center gap-1 pr-2">
                  <button
                    type="button"
                    onClick={() => onEditUser(user)}
                    className="flex min-h-[60px] min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 text-left active:bg-neutral-50"
                  >
                    <Avatar user={user} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className={`truncate text-[14px] ${user.isActive ? 'text-ink' : 'text-neutral-400'}`}>
                          {displayName(user)}
                        </span>
                        {!user.isActive && <Pill tone="neutral">Inactive</Pill>}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-[12px] text-neutral-400">
                        <span className="truncate">{user.email}</span>
                      </span>
                    </span>
                    <span className="flex flex-shrink-0 flex-col items-end gap-1">
                      <RolePill role={user.role} />
                      <span className="text-[11px] tabular-nums text-neutral-400">{formatRelative(user.lastLoginAt)}</span>
                    </span>
                  </button>
                  {rowMenu(user)}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between gap-3 border-t border-neutral-200/70 px-4 py-2">
            <span className="text-[12px] tabular-nums text-neutral-400">
              {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </span>
            <div className="flex items-center gap-1">
              <IconButton
                label="Previous page"
                size="sm"
                onClick={() => setQuery(prev => ({ ...prev, page: page - 1 }))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
              <IconButton
                label="Next page"
                size="sm"
                onClick={() => setQuery(prev => ({ ...prev, page: page + 1 }))}
                disabled={page * limit >= total}
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
            </div>
          </div>
        )}
      </Card>
    </>
  );
};
